import { Logout, PersonSearch, SwitchAccount } from '@mui/icons-material';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useSession } from '@side/next-auth/react';
import { Tooltip, UserAvatar, useIsMobile } from '@side/pantry';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useIdentity } from '$api-clients/hooks';
import { useViewReportAs } from '../../helpers';
import { useViewerReportsConfiguration } from '../../ReportsConfiguration/useReportsConfiguration';
import { ViewAsDialog } from './ViewAsDialog';

export function ViewAsButton({
  size,
  forceIconOnly = false,
}: {
  size: 'small' | 'medium';
  forceIconOnly?: boolean;
}) {
  const { data: session } = useSession();
  const isMobile = useIsMobile('sm');
  const router = useRouter();
  const viewReportAs = useViewReportAs();
  const isAdmin = session?.user?.roles?.includes('ADMIN') ?? false;
  const hasReportingScope = session?.hasScopes?.(['reports:read']) ?? false;
  // The button gates on the *viewer's* config — never the viewed-as user's —
  // so a partner viewing as a regular agent still sees the control.
  const { data: viewerConfig } = useViewerReportsConfiguration();
  const isPartner =
    viewerConfig?.highestRole === 'PARTNER_AGENT' ||
    viewerConfig?.highestRole === 'PARTNER_AGENT_WITH_SUBTEAM';
  const isAuthenticatedUser = session?.user?.id === viewReportAs;
  const isViewingAsOther = !isAuthenticatedUser && !!viewReportAs;
  const canRender =
    (hasReportingScope && isAdmin) ||
    (isPartner && !!viewerConfig?.activeTeamMemberIdentityUserIds.length);
  // Admins keep the unrestricted global search even if they also happen to be
  // a partner-lead on a test team — otherwise they'd only see that test team.
  const restrictToUserIds =
    !isAdmin && isPartner
      ? viewerConfig?.activeTeamMemberIdentityUserIds
      : undefined;

  const { data: viewedUser } = useIdentity(
    '/users/{userId}',
    isViewingAsOther && viewReportAs
      ? { params: { path: { userId: viewReportAs } } }
      : null,
  );

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const isSmall = size === 'small';
  const shouldRenderIconOnly = forceIconOnly || (isMobile && isSmall);
  const iconButtonSize: 'small' | 'medium' =
    isSmall && !forceIconOnly ? 'small' : 'medium';
  const iconButtonSizePx = isSmall && !forceIconOnly ? '31px' : '40px';
  const iconButtonSx = (t: Theme) => ({
    borderRadius: '2px',
    border: `1.5px solid ${t.palette.other.outlineBorder}`,
    backgroundColor: t.palette.common.white,
    height: iconButtonSizePx,
    width: iconButtonSizePx,
    '&:hover': {
      backgroundColor: t.palette.common.white,
    },
  });

  if (!canRender) {
    return null;
  }

  const handleExitViewAs = async () => {
    setMenuAnchor(null);
    const { viewReportAs: _, ...rest } = router.query;
    await router.push({ pathname: router.pathname, query: rest }, undefined, {
      scroll: true,
    });
  };

  const handleSwitchViewAs = () => {
    setMenuAnchor(null);
    setDialogOpen(true);
  };

  const handleUserSelected = async (userId: string) => {
    setDialogOpen(false);
    if (userId === session?.user?.id) {
      const { viewReportAs: _, ...rest } = router.query;
      await router.push({ pathname: router.pathname, query: rest }, undefined, {
        scroll: true,
      });
    } else {
      await router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, viewReportAs: userId },
        },
        undefined,
        {
          scroll: true,
        },
      );
    }
  };

  if (isViewingAsOther && viewedUser) {
    return (
      <>
        <Tooltip title={`Viewing report as ${viewedUser.displayName}`}>
          {shouldRenderIconOnly ? (
            <IconButton
              size={iconButtonSize}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={iconButtonSx}
            >
              <UserAvatar
                sx={{ width: 22, height: 22, fontSize: '12px !important' }}
                avatarUrl={viewedUser.photoUrl ?? undefined}
                name={viewedUser.displayName}
              />
            </IconButton>
          ) : (
            <Button
              size={size}
              color="utility"
              variant="outlined"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              startIcon={
                <UserAvatar
                  sx={{
                    width: isSmall ? 24 : 28,
                    height: isSmall ? 24 : 28,
                    fontSize: `${isSmall ? 14 : 16}px !important`,
                  }}
                  avatarUrl={viewedUser.photoUrl ?? undefined}
                  name={viewedUser.displayName}
                />
              }
              sx={{
                maxWidth: { xs: 132, md: 200 },
                minWidth: 0,
                overflow: 'hidden',
                '& .MuiButton-startIcon': { flexShrink: 0 },
              }}
            >
              <Typography component="span" noWrap>
                {viewedUser.displayName}
              </Typography>
            </Button>
          )}
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleSwitchViewAs}>
            <ListItemIcon>
              <SwitchAccount fontSize="small" />
            </ListItemIcon>
            <ListItemText>Switch View As</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExitViewAs}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText>Exit View As</ListItemText>
          </MenuItem>
        </Menu>
        <ViewAsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSelect={handleUserSelected}
          restrictToUserIds={restrictToUserIds}
        />
      </>
    );
  }

  return (
    <>
      <Tooltip title="View report as another user">
        {shouldRenderIconOnly ? (
          <IconButton
            size={iconButtonSize}
            onClick={() => setDialogOpen(true)}
            sx={iconButtonSx}
          >
            <PersonSearch fontSize="small" />
          </IconButton>
        ) : (
          <Button
            size={size}
            variant="outlined"
            startIcon={<PersonSearch fontSize="small" />}
            onClick={() => setDialogOpen(true)}
            color="utility"
            sx={{
              textWrap: 'nowrap',
            }}
          >
            View As
          </Button>
        )}
      </Tooltip>
      <ViewAsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleUserSelected}
        restrictToUserIds={restrictToUserIds}
      />
    </>
  );
}
